import { merge } from 'lodash-es';

import { resources as udacityApplicationResources } from '@udacity/application/i18n';
import { resources as udacityAssessmentResources } from '@udacity/assessment/i18n';
import { resources as udacityCertificateResources } from '@udacity/udacity-certificate/i18n';
import { resources as udacityHeaderResources } from '@udacity/udacity-header-v2/i18n';

import { languages } from './languages';

/**
 * Map from i18n bundle key (used in addResourceBundle) to the dynamic import for that locale.
 * Only en-US is loaded at init; all others are loaded on demand.
 */
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  ar: () => import('./locales/ar.json'),
  de: () => import('./locales/de.json'),
  el: () => import('./locales/el.json'),
  'en-US': () => import('./locales/en-US.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  'fr-CA': () => import('./locales/fr-CA.json'),
  'id-ID': () => import('./locales/id-ID.json'),
  it: () => import('./locales/it.json'),
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  ku: () => import('./locales/ku.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  'pt-PT': () => import('./locales/pt-PT.json'),
  ru: () => import('./locales/ru.json'),
  tr: () => import('./locales/tr.json'),
};

/** Normalize detector/output language codes to our bundle keys. */
const languageToBundleKey: Record<string, string> = {
  ar: 'ar',
  'ar-SA': 'ar',
  de: 'de',
  'de-DE': 'de',
  el: 'el',
  'el-GR': 'el',
  en: 'en-US',
  'en-US': 'en-US',
  es: 'es',
  'es-ES': 'es',
  fr: 'fr',
  'fr-FR': 'fr',
  'fr-CA': 'fr-CA',
  id: 'id-ID',
  'id-ID': 'id-ID',
  it: 'it',
  'it-IT': 'it',
  ja: 'ja',
  'ja-JP': 'ja',
  ko: 'ko',
  'ko-KR': 'ko',
  ku: 'ku',
  'ku-IR': 'ku',
  pt: 'pt-PT',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-PT',
  ru: 'ru',
  'ru-RU': 'ru',
  tr: 'tr',
  'tr-TR': 'tr',
};

export function getBundleKeyForLanguage(lng: string): string {
  return languageToBundleKey[lng] ?? languages.enUS.id;
}

const loadPromises = new Map<string, Promise<void>>();

/**
 * Load translations for a bundle key and add them to the i18n instance.
 * Caller should pass the bundle key they already have (e.g. from getBundleKeyForLanguage).
 * Returns a promise that resolves when the bundle is added. Deduplicates in-flight loads.
 */
export function loadTranslationsForLanguage(i18n: import('i18next').i18n, bundleKey: string): Promise<void> {
  if (i18n.hasResourceBundle(bundleKey, 'translation')) {
    return Promise.resolve();
  }

  let promise = loadPromises.get(bundleKey);
  if (promise) return promise;

  const loader = localeLoaders[bundleKey];
  if (!loader) {
    return Promise.resolve();
  }

  type LngResource = { translation?: Record<string, unknown> };
  const cert = (udacityCertificateResources as Record<string, LngResource>)[bundleKey]?.translation;
  const header = (udacityHeaderResources as Record<string, LngResource>)[bundleKey]?.translation;
  const app = (udacityApplicationResources as Record<string, LngResource>)[bundleKey]?.translation;
  const assessment = (udacityAssessmentResources as Record<string, LngResource>)[bundleKey]?.translation;

  promise = loader()
    .then((mod) => {
      const ourTranslation = mod.default as Record<string, unknown>;
      const merged = merge({}, ourTranslation, cert, header, app, assessment);
      i18n.addResourceBundle(bundleKey, 'translation', merged);
      if (bundleKey === 'pt-PT') {
        i18n.addResourceBundle('pt', 'translation', merged);
      }
    })
    .finally(() => {
      loadPromises.delete(bundleKey);
    });

  loadPromises.set(bundleKey, promise);
  return promise;
}

/**
 * Return the current in-flight load promise for a bundle key, if any.
 * Caller should pass the bundle key they already have (e.g. from getBundleKeyForLanguage).
 * Used by Suspense to throw the same promise until the bundle is ready.
 */
export function getLoadPromiseForLanguage(i18n: import('i18next').i18n, bundleKey: string): Promise<void> | null {
  if (i18n.hasResourceBundle(bundleKey, 'translation')) return null;
  return loadPromises.get(bundleKey) ?? null;
}
